import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { TimelineMax, Power2 } from "gsap";
import FlipMatchCard from "../../components/FlipMatchCard/FlipMatchCard";
import IconRocket from "../../components/svg/IconRocket";
import FlipMatchSelect from "../FlipMatchSelect/FlipMatchSelect";
import { FLIP_MATCH_STATE_TYPE } from "../FlipMatch/constants";
import { chooseCard, setCards } from "../FlipMatch/thunks";

const CARD_WIDTH = 100;
const CARD_HEIGHT = 140;
const STACK_OFFSET = 3;
const DEAL_DURATION = 0.45;
const DEAL_STAGGER = 0.04;

class FlipMatchCardList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            phase: "measure",
            cardPositions: [],
            containerRect: null,
        };
        this.containerRef = React.createRef();
        this.cardRefs = [];
    }

    static getDerivedStateFromProps(props, state) {
        const trigger = String(props.flip.dealTrigger || 0) + (props.flip.collection?.name || "");
        if (state._lastTrigger !== trigger) {
            return { _lastTrigger: trigger, phase: "measure", cardPositions: [], containerRect: null };
        }
        return null;
    }

    componentDidMount() {
        this.measureOrAnimate();
    }

    componentDidUpdate(prevProps, prevState) {
        this.measureOrAnimate(prevState);
    }

    measureOrAnimate = (prevState = {}) => {
        const { phase } = this.state;
        if (phase === "measure" && this.containerRef.current) {
            const count = Object.values(this.props.flip.collection.cards || {}).length;
            if (count === 0) {
                this.setState({ phase: "done" });
                return;
            }
            const refsToUse = this.cardRefs.slice(0, count);
            const refsReady = refsToUse.length === count && refsToUse.every((r) => r && r.current);
            if (!refsReady) return;
            const readLayout = (retryCount = 0) => {
                if (!this.containerRef.current) return;
                const maxRetries = 10;
                const allRefsMounted = refsToUse.every((r) => r && r.current);
                if (!allRefsMounted) {
                    if (retryCount < maxRetries) {
                        requestAnimationFrame(() => readLayout(retryCount + 1));
                    }
                    return;
                }
                const containerRect = this.containerRef.current.getBoundingClientRect();
                const cardPositions = refsToUse.map((ref) => {
                    const r = ref.current.getBoundingClientRect();
                    return {
                        left: r.left - containerRect.left,
                        top: r.top - containerRect.top,
                    };
                });
                const hasProgress = (this.props.flip.flipped || []).length > 0;
                this.setState({ phase: hasProgress ? "dealing" : "stack", cardPositions, containerRect });
            };
            requestAnimationFrame(() => requestAnimationFrame(() => readLayout(0)));
            return;
        }
        if (phase === "dealing" && (prevState.phase === "stack" || prevState.phase === "measure") && this.state.containerRect) {
            requestAnimationFrame(() => this.runDealAnimation());
        }
    };

    runDealAnimation = () => {
        const { containerRect, cardPositions } = this.state;
        const container = this.containerRef.current;
        if (!container || !containerRect || cardPositions.length === 0) {
            this.setState({ phase: "done" });
            return;
        }
        const wrappers = container.querySelectorAll(".flip-card-deal-wrapper");
        if (wrappers.length === 0) {
            this.setState({ phase: "done" });
            return;
        }
        const tl = new TimelineMax({
            onComplete: () => this.setState({ phase: "done" }),
        });
        wrappers.forEach((el, i) => {
            const pos = cardPositions[i];
            if (!pos) return;
            const finalLeft = containerRect.left + pos.left;
            const finalTop = containerRect.top + pos.top;
            tl.to(
                el,
                DEAL_DURATION,
                {
                    left: finalLeft,
                    top: finalTop,
                    ease: Power2.easeOut,
                },
                i * DEAL_STAGGER
            );
        });
    };

    handleStartClick = () => {
        this.setState({ phase: "dealing" });
    };

    handleFlipCardClick = (card) => {
        const { chooseCardAction } = this.props;
        chooseCardAction(card);
    };

    render() {
        const { flip, className, itemClassName } = this.props;
        const { phase, cardPositions, containerRect } = this.state;
        const cards = Object.values(flip.collection.cards || {});
        const isMeasuring = phase === "measure";
        const isStackOrDealing = phase === "stack" || phase === "dealing";
        const isWaitingForStart = phase === "stack";
        const isDoneWithPositions = phase === "done" && containerRect && cardPositions.length === cards.length;
        const restackForReset = isDoneWithPositions && flip.complete;

        const cardList = cards.map((item, i) => {
            const commonProps = {
                key: item.name,
                name: item.name,
                value: item.name,
                group: item.group,
                frontIcon: item.frontIcon,
                frontText: item.frontText,
                backIcon: item.backIcon,
                backText: item.backText,
                theme: item.theme,
                flipped: flip.flipped.some((value) => value.name === item.name),
                selected: flip.selected.some((value) => value.name === item.name),
                current: flip.current && flip.current.name === item.name,
                onClick: this.handleFlipCardClick,
                className: itemClassName,
            };

            if (isMeasuring) {
                while (this.cardRefs.length <= i) this.cardRefs.push(React.createRef());
                return (
                    <div key={item.name} ref={this.cardRefs[i]} className={itemClassName} style={{ visibility: "hidden" }}>
                        <FlipMatchCard {...commonProps} />
                    </div>
                );
            }

            if ((isStackOrDealing || isDoneWithPositions) && containerRect && cardPositions[i]) {
                const stackX = containerRect.width / 2 - CARD_WIDTH / 2;
                const stackY = containerRect.height / 2 - CARD_HEIGHT / 2;
                const pos = cardPositions[i];
                const atStack = isStackOrDealing;
                const isDealingFromViewportCenter = phase === "dealing";
                const left = isDealingFromViewportCenter
                    ? (typeof window !== "undefined" ? window.innerWidth / 2 - CARD_WIDTH / 2 : 0) + i * STACK_OFFSET
                    : atStack
                    ? stackX + i * STACK_OFFSET
                    : pos.left;
                const top = isDealingFromViewportCenter
                    ? (typeof window !== "undefined" ? window.innerHeight / 2 - CARD_HEIGHT / 2 : 0) + i * STACK_OFFSET
                    : atStack
                    ? stackY + i * STACK_OFFSET
                    : pos.top;
                return (
                    <div
                        key={item.name}
                        className="flip-card-deal-wrapper"
                        style={{
                            position: isDealingFromViewportCenter ? "fixed" : "absolute",
                            left,
                            top,
                            width: CARD_WIDTH,
                            height: CARD_HEIGHT,
                            pointerEvents: isWaitingForStart ? "none" : "auto",
                            visibility: isWaitingForStart ? "hidden" : undefined,
                        }}
                    >
                        <FlipMatchCard {...commonProps} className={itemClassName} />
                    </div>
                );
            }

            return <FlipMatchCard key={item.name} {...commonProps} />;
        });

        if (cards.length === 0) {
            return (
                <div className={className}>
                    <h2>{this.props.t("no_cards_found")}</h2>
                </div>
            );
        }

        if (isMeasuring) {
            return (
                <div ref={this.containerRef} className={className} style={{ visibility: "hidden" }}>
                    {cardList}
                </div>
            );
        }

        if ((isStackOrDealing || isDoneWithPositions) && containerRect) {
            const hideStackWhenComplete = restackForReset;
            const cardArea = (
                <div
                    ref={this.containerRef}
                    className={className}
                    style={{
                        position: "relative",
                        minHeight: containerRect.height,
                        minWidth: containerRect.width,
                        visibility: hideStackWhenComplete ? "hidden" : undefined,
                    }}
                >
                    {isWaitingForStart && (
                        <div
                            className="flip-cards-start-wrap"
                            style={{
                                position: "fixed",
                                left: "50%",
                                top: "50%",
                                transform: "translateX(-50%) translateY(-40%)",
                                zIndex: 10,
                            }}
                        >
                            <button
                                type="button"
                                className="btn btn-primary flip-cards-start-btn"
                                onClick={this.handleStartClick}
                            >
                                <IconRocket width={32} height={32} style={{ marginRight: 8 }} aria-hidden />
                                {this.props.t("start")}
                            </button>
                            <div className="flip-cards-start-select-wrap">
                                <FlipMatchSelect
                                    className="form-control flip-cards-start-select"
                                    emptyOption={this.props.t("choose_card_collection")}
                                />
                            </div>
                        </div>
                    )}
                    <div style={{ width: containerRect.width, height: containerRect.height, pointerEvents: isWaitingForStart ? "none" : "auto" }} aria-hidden="true" />
                    {cardList}
                </div>
            );
            if (isWaitingForStart) {
                return (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 5,
                        }}
                    >
                        {cardArea}
                    </div>
                );
            }
            return cardArea;
        }

        return <div className={className}>{cardList}</div>;
    }
}

FlipMatchCardList.propTypes = {
    chooseCardAction: PropTypes.func.isRequired,
    flip: FLIP_MATCH_STATE_TYPE,
    className: PropTypes.string,
    itemClassName: PropTypes.string,
    t: PropTypes.func.isRequired,
};

FlipMatchCardList.defaultProps = {
    flip: {},
    className: "",
    itemClassName: "",
};

const mapDispatchToProps = (dispatch) => ({
    chooseCardAction: (card) => dispatch(chooseCard(card)),
    setCardsAction: (cards) => dispatch(setCards(cards)),
});

export default connect(null, mapDispatchToProps)(withTranslation()(FlipMatchCardList));
